/**
 * PatientModal Component
 *
 * Modal dialog for displaying detailed patient information with two tabs:
 * 1. Outcome Measures - List of clinical quality measures with expandable details
 * 2. PIMs Data - Medication complexity scores and safety flags
 *
 * Follows the Dialog pattern from shadcn/ui (same as StatCard modal).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ExternalLink, Mail, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { MeasureCard } from './components/MeasureCard'
import { PIMsDataTab } from './components/PIMsDataTab'
import { AskQuestionModal } from './components/AskQuestionModal'
import { usePatientMeasures } from '@/hooks/equip/useEquipPatient'
import type { PatientMeasureDetail } from '@/lib/equip-types'

interface PatientModalProps {
  patientId: string | null
  defaultTab?: 'measures' | 'pims'
  onClose: () => void
  year?: number
  quarter?: number
  providerName?: string
  providerEmail?: string
}

export function PatientModal({
  patientId,
  defaultTab = 'measures',
  onClose,
  year,
  quarter,
  providerName,
  providerEmail,
}: PatientModalProps) {
  const [activeTab, setActiveTab] = useState(defaultTab)
  const [expandedMeasures, setExpandedMeasures] = useState<Set<string>>(new Set())
  const [showQuestionModal, setShowQuestionModal] = useState(false)
  const [tabContentHeight, setTabContentHeight] = useState<number | 'auto'>('auto')
  const tabObserverRef = useRef<ResizeObserver | null>(null)

  const tabMeasureRef = useCallback((node: HTMLDivElement | null) => {
    if (tabObserverRef.current) tabObserverRef.current.disconnect()
    if (!node) return
    setTabContentHeight(node.scrollHeight)
    tabObserverRef.current = new ResizeObserver(() => {
      setTabContentHeight(node.scrollHeight)
    })
    tabObserverRef.current.observe(node)
  }, [])

  // React Query: fetch patient measures — cache hit within 5 minutes on re-open
  // enabled: !!patientId ensures no request fires when modal is closed (patientId is null)
  const patientQuery = usePatientMeasures(
    patientId ?? undefined,
    year ?? new Date().getFullYear(),
    quarter ?? Math.ceil((new Date().getMonth() + 1) / 3)
  )

  const patientData: PatientMeasureDetail | null = patientQuery.data ?? null
  const isLoading = patientQuery.isLoading
  const error = patientQuery.error?.message ?? null

  // Reset expanded measures when modal closes (patientId becomes null)
  useEffect(() => {
    if (!patientId) {
      setExpandedMeasures(new Set())
    }
  }, [patientId])

  const toggleExpanded = (measureName: string) => {
    setExpandedMeasures((prev) => {
      const next = new Set(prev)
      if (next.has(measureName)) {
        next.delete(measureName)
      } else {
        next.add(measureName)
      }
      return next
    })
  }

  return (
    <>
      <Dialog open={patientId !== null} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-4xl min-h-[50vh] max-h-[80vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {isLoading ? (
                <Skeleton className="h-8 w-64" />
              ) : patientData ? (
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-2xl font-semibold">
                      {patientData.patientName || 'Unknown Patient'}
                    </div>
                    <a
                      href={`https://eventus.afoundria.com/patient/show/${patientData.patientId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-1 text-sm"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open Chart
                    </a>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {patientData.facilityName || patientData.facilityId || 'N/A'}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Patient ID: {patientData.patientId}</div>
                </div>
              ) : (
                <div className="text-muted-foreground">Patient not found</div>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0">
              <div>
                {isLoading && (
                  <div className="space-y-3 mt-4">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 p-4 text-sm text-destructive bg-destructive/10 rounded-md mt-4">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {!isLoading && patientData && (
                  <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'measures' | 'pims')} className="mt-4">
                    <TabsList>
                      <TabsTrigger value="measures">Outcome Measures</TabsTrigger>
                      <TabsTrigger value="pims">PIMs Data</TabsTrigger>
                    </TabsList>

                    <motion.div
                      animate={{ height: tabContentHeight }}
                      initial={false}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div ref={tabMeasureRef} className="pt-4">
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={activeTab}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, ease: 'easeInOut' }}
                          >
                            {activeTab === 'measures' ? (
                              patientData.measures.length > 0 ? (
                                <div className="space-y-3">
                                  {patientData.measures.map((measure) => (
                                    <MeasureCard
                                      key={measure.measureName}
                                      measure={measure}
                                      isExpanded={expandedMeasures.has(measure.measureName)}
                                      onToggle={() => toggleExpanded(measure.measureName)}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                  No outcome measures found for this patient
                                </div>
                              )
                            ) : (
                              <PIMsDataTab patientId={patientData.patientId} year={year || new Date().getFullYear()} quarter={quarter || Math.ceil((new Date().getMonth() + 1) / 3)} />
                            )}
                          </motion.div>
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  </Tabs>
                )}
              </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowQuestionModal(true)}
              disabled={!patientData}
            >
              <Mail className="mr-2 h-4 w-4" />
              Ask a Question
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AskQuestionModal rendered outside PatientModal's Dialog to avoid nesting */}
      {patientData && (
        <AskQuestionModal
          open={showQuestionModal}
          onClose={() => setShowQuestionModal(false)}
          context={{
            type: 'patient',
            identifier: patientData.patientId,
            providerName: providerName || 'Unknown',
            providerEmail: providerEmail || '',
            year: year || new Date().getFullYear(),
            quarter: quarter || Math.ceil((new Date().getMonth() + 1) / 3),
            facilityName: patientData.facilityName || patientData.facilityId || undefined,
          }}
          patientMeasures={patientData.measures}
        />
      )}
    </>
  )
}
